// The QuoteFlow plan in the console (docs/10 §12):
//   useQuoteAccess  whether the company has the add-on and has accepted the quote terms
//   QuoteGate       wraps every Quotes page: without the add-on, an explanation instead
//   QuoteTermsCard  the quote terms, to read and accept (Quote settings; before sending)
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Check, FileCheck2, FileText, Lock } from "lucide-react";
import { QUOTE_TERMS, QUOTE_TERMS_TITLE, QUOTE_TERMS_VERSION } from "../quoteTerms";
import { useQuotesApi, type QuoteAccess } from "./api";
import { when } from "./format";

interface AccessState {
  access: QuoteAccess | null;
  error: string | null;
  setAccess: (a: QuoteAccess) => void;
}

const AccessContext = createContext<AccessState>({ access: null, error: null, setAccess: () => undefined });

/** The add-on and terms status, loaded by QuoteGate for the page inside it. */
export function useQuoteAccess() {
  return useContext(AccessContext);
}

/** Loads the access status; shows the add-on's explanation to companies without it. */
export function QuoteGate({ children }: { children: ReactNode }) {
  const api = useQuotesApi();
  const [access, setAccess] = useState<QuoteAccess | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api
      .getAccess()
      .then(setAccess)
      .catch((e: Error) => setError(e.message));
  }, [api]);

  if (error) return <div className="page"><div className="flash flash-danger">{error}</div></div>;
  if (!access) return <div className="page"><p className="muted">Loading…</p></div>;
  if (!access.entitled) return <QuoteUpsell />;
  return <AccessContext.Provider value={{ access, error, setAccess }}>{children}</AccessContext.Provider>;
}

/** What the QuoteFlow plan adds, for companies that don't have it. */
function QuoteUpsell() {
  return (
    <div className="page q-page">
      <div className="page-head">
        <h1>Quotes</h1>
        <p>Turn estimates into proper quotes: your products, your prices and margins, sent to your customer to accept online.</p>
      </div>
      <section className="card q-upsell">
        <div className="q-upsell-icon" aria-hidden="true">
          <Lock size={22} />
        </div>
        <h2>QuoteFlow</h2>
        {/* Quoting is heat pumps only for now; solar quoting is on the roadmap */}
        <div className="flash flash-info">
          <span>
            QuoteFlow currently builds <strong>heat pump (ASHP) quotes</strong>. Solar quotes are planned for release in November 2026.
          </span>
        </div>
        <p className="card-sub">Your estimates stay as they are: every website component sizes the heat pump from the home's energy certificate and prices it from your size price list. QuoteFlow adds:</p>
        <ul className="q-upsell-list">
          {[
            "Your catalogue: heat pumps, cylinders and parts at your cost prices (import your merchant's price list, or start from ours)",
            "Your pricing: labour and materials, markup or margin, Good / Better / Best packages, extras",
            "Quotes built in minutes from a postcode, sized from the heat loss, with your profit on every option",
            "A branded quote page your customer can accept online, with running costs, the grant and finance",
            "Instant detailed quotes on your Full width and hosted page components, straight after the estimate",
          ].map((t) => (
            <li key={t}>
              <Check size={16} aria-hidden="true" /> {t}
            </li>
          ))}
        </ul>
        <p className="muted">£219 a month plus VAT, and it includes LeadFlow ASHP and LeadFlow Solar (up to 3 estimate components each), with single page hosting for both.</p>
        <Link to="/billing" className="btn btn-primary">
          Choose QuoteFlow
        </Link>
      </section>
    </div>
  );
}

/** The quote terms: accept them (or see who did). */
export function QuoteTermsCard({ compact = false }: { compact?: boolean }) {
  const api = useQuotesApi();
  const { access, setAccess } = useQuoteAccess();
  const [ticked, setTicked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(!compact);

  const accept = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      setAccess(await api.acceptTerms(QUOTE_TERMS_VERSION));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, [api, setAccess]);

  if (!access) return null;
  if (access.termsAccepted && access.acceptance) {
    return (
      <section className="card q-terms q-terms-ok">
        <div className="q-card-head">
          <h2>
            <FileCheck2 size={18} aria-hidden="true" /> Quote terms accepted
          </h2>
          <button type="button" className="link-button small" onClick={() => setOpen((o) => !o)}>
            {open ? "Hide" : "Read them"}
          </button>
        </div>
        <p className="muted">
          By {access.acceptance.acceptedByEmail || "your team"}, {when(access.acceptance.acceptedAt)} (version {access.acceptance.version}).
        </p>
        {open && <TermsText />}
      </section>
    );
  }
  return (
    <section className="card q-terms">
      <h2>
        <FileText size={18} aria-hidden="true" /> {QUOTE_TERMS_TITLE}
      </h2>
      <p className="card-sub">
        Read and accept these before you send a quote or switch on instant quotes on a website component. They're separate from the estimate terms each component has.
        {access.acceptance ? " The wording has changed since you last accepted it." : ""}
      </p>
      <TermsText />
      <label className="q-check q-terms-tick">
        <input type="checkbox" checked={ticked} onChange={(e) => setTicked(e.target.checked)} />
        I've checked my catalogue, packages and prices, and I accept these terms for my company's quotes
      </label>
      {error && <div className="flash flash-danger">{error}</div>}
      <button type="button" className="btn btn-primary" disabled={!ticked || busy} onClick={() => void accept()}>
        {busy ? "Saving…" : "Accept the quote terms"}
      </button>
    </section>
  );
}

function TermsText() {
  return (
    <ol className="q-terms-text">
      {QUOTE_TERMS.map((t) => (
        <li key={t.heading}>
          <strong>{t.heading}.</strong> {t.body}
        </li>
      ))}
    </ol>
  );
}
